import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export interface HelpCategory {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface HelpArticle {
  id: string;
  category_id: string;
  title: string;
  summary: string | null;
  body: string;
  visibility: 'all' | 'mentor' | 'entrepreneur' | 'hidden';
  is_published: boolean;
  is_archived: boolean;
  archived_at: string | null;
  archived_by: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  category?: HelpCategory;
}

export interface HelpSettings {
  id: string;
  help_enabled: boolean;
  show_in_navigation: boolean;
  updated_at: string;
  updated_by: string | null;
}

// Fetch help settings
export function useHelpSettings() {
  return useQuery({
    queryKey: ["help-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_settings")
        .select("*")
        .single();

      if (error) throw error;
      return data as HelpSettings;
    },
  });
}

// Update help settings (admin only)
export function useUpdateHelpSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Partial<Pick<HelpSettings, 'help_enabled' | 'show_in_navigation'>>) => {
      // This was an UNFILTERED update — it only ever worked because the table
      // holds exactly one row. Scope it to the row we actually read.
      const { data: existing, error: readError } = await supabase
        .from("help_settings")
        .select("id")
        .limit(1)
        .maybeSingle();

      if (readError) throw readError;
      if (!existing) throw new Error("Help settings row is missing.");

      const { data, error } = await supabase
        .from("help_settings")
        .update(settings)
        .eq("id", existing.id)
        .select()
        .single();

      if (error) throw error;
      return data as HelpSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-settings"] });
    },
  });
}

// Fetch all help categories
export function useHelpCategories() {
  return useQuery({
    queryKey: ["help-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_categories")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data as HelpCategory[];
    },
  });
}

// Fetch all help articles (for users - filtered by RLS)
export function useHelpArticles(categoryId?: string) {
  return useQuery({
    queryKey: ["help-articles", categoryId],
    queryFn: async () => {
      let query = supabase
        .from("help_articles")
        .select(`
          *,
          category:help_categories(*)
        `)
        // Archived articles were still listed publicly: neither this hook nor the
        // RLS policy referenced is_archived (useRecentHelpArticles does).
        .eq("is_archived", false)
        .order("sort_order", { ascending: true });

      if (categoryId) {
        query = query.eq("category_id", categoryId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as (HelpArticle & { category: HelpCategory })[];
    },
  });
}

// Fetch single article
export function useHelpArticle(articleId: string) {
  return useQuery({
    queryKey: ["help-article", articleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_articles")
        .select(`
          *,
          category:help_categories(*)
        `)
        .eq("id", articleId)
        .single();

      if (error) throw error;
      return data as HelpArticle & { category: HelpCategory };
    },
    enabled: !!articleId,
  });
}

// Admin: Fetch all articles including hidden/drafts
export function useAdminHelpArticles() {
  return useQuery({
    queryKey: ["admin-help-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_articles")
        .select(`
          *,
          category:help_categories(*)
        `)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data as (HelpArticle & { category: HelpCategory })[];
    },
  });
}

// Admin: Create category
export function useCreateHelpCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (category: Pick<HelpCategory, 'name' | 'description' | 'icon' | 'sort_order'>) => {
      const { data, error } = await supabase
        .from("help_categories")
        .insert(category)
        .select()
        .single();

      if (error) throw error;
      return data as HelpCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-categories"] });
    },
  });
}

// Admin: Update category
export function useUpdateHelpCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<HelpCategory> & { id: string }) => {
      const { data, error } = await supabase
        .from("help_categories")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as HelpCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-categories"] });
    },
  });
}

// Admin: Delete category
export function useDeleteHelpCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("help_categories")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-categories"] });
    },
  });
}

// Admin: Create article
export function useCreateHelpArticle() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (article: Pick<HelpArticle, 'category_id' | 'title' | 'summary' | 'body' | 'visibility' | 'is_published' | 'sort_order'>) => {
      const { data, error } = await supabase
        .from("help_articles")
        .insert({
          ...article,
          created_by: user?.id,
          updated_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as HelpArticle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-articles"] });
      queryClient.invalidateQueries({ queryKey: ["admin-help-articles"] });
    },
  });
}

// Admin: Update article
export function useUpdateHelpArticle() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<HelpArticle> & { id: string }) => {
      const { data, error } = await supabase
        .from("help_articles")
        .update({
          ...updates,
          updated_by: user?.id,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as HelpArticle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-articles"] });
      queryClient.invalidateQueries({ queryKey: ["admin-help-articles"] });
      queryClient.invalidateQueries({ queryKey: ["help-article"] });
    },
  });
}

// Admin: Delete article
export function useDeleteHelpArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("help_articles")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-articles"] });
      queryClient.invalidateQueries({ queryKey: ["admin-help-articles"] });
    },
  });
}

// Admin: Duplicate article
export function useDuplicateHelpArticle() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (article: HelpArticle) => {
      const { data, error } = await supabase
        .from("help_articles")
        .insert({
          category_id: article.category_id,
          title: `${article.title} (Copy)`,
          summary: article.summary,
          body: article.body,
          visibility: article.visibility,
          is_published: false, // Duplicates start as drafts
          is_archived: false,
          sort_order: article.sort_order + 1,
          created_by: user?.id,
          updated_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as HelpArticle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-articles"] });
      queryClient.invalidateQueries({ queryKey: ["admin-help-articles"] });
    },
  });
}

// Admin: Archive/unarchive article
export function useArchiveHelpArticle() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, archive }: { id: string; archive: boolean }) => {
      const { data, error } = await supabase
        .from("help_articles")
        .update({
          is_archived: archive,
          archived_at: archive ? new Date().toISOString() : null,
          archived_by: archive ? user?.id : null,
          updated_by: user?.id,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as HelpArticle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-articles"] });
      queryClient.invalidateQueries({ queryKey: ["admin-help-articles"] });
      queryClient.invalidateQueries({ queryKey: ["help-article"] });
    },
  });
}

// Admin: Reorder articles
export function useReorderHelpArticles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (articles: { id: string; sort_order: number }[]) => {
      for (const article of articles) {
        const { error } = await supabase
          .from("help_articles")
          .update({ sort_order: article.sort_order })
          .eq("id", article.id);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-articles"] });
      queryClient.invalidateQueries({ queryKey: ["admin-help-articles"] });
    },
  });
}

// Admin: Reorder categories
export function useReorderHelpCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categories: { id: string; sort_order: number }[]) => {
      for (const category of categories) {
        const { error } = await supabase
          .from("help_categories")
          .update({ sort_order: category.sort_order })
          .eq("id", category.id);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["help-categories"] });
    },
  });
}

// Fetch recent help articles (published, non-archived, ordered by created_at)
export function useRecentHelpArticles(limit: number = 5) {
  return useQuery({
    queryKey: ["help-articles-recent", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_articles")
        .select(`
          *,
          category:help_categories(*)
        `)
        .eq("is_published", true)
        .eq("is_archived", false)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as (HelpArticle & { category: HelpCategory })[];
    },
  });
}
